import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import { Home, Wrench, ShieldCheck, ShieldX, ArrowRight, Info, ImagePlus, Send, Gauge, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../../utils/useDateFormat';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Vehicle, VehicleStatus } from '../../../../../shared/types/vehicle.types';
import { VehicleService } from '../../../../../shared/types/vehicle-service.types';
import { VehicleTransfer } from '../../../../../shared/types/vehicle-transfer.types';
import { SettingListItem } from '../../../../../shared/types/setting.types';
import useSettingLists from '../../../wrappers/use-setting-list-item';
import useVehicle from '../../../wrappers/use-vehicle';
import useVehicleService from '../../../wrappers/use-vehicle-service';
import useVehicleTransfer from '../../../wrappers/use-vehicle-transfer';
import useOdometerHistory from '../../../wrappers/use-odometer-history';
import VehiclePicture from '../../../components/VehiclePicture';
import VehiclePictureUploadDialog from '../../../components/VehiclePictureUploadDialog';
import OdometerModal from '../../../components/OdometerModal';
import ServiceHistory from './ServiceHistory';
import TransferHistory from './TransferHistory';
import Documents from './Documents';
import {
    getVehicleDetailTransferTabStyles,
    vehicleDetailStyles,
} from './styles';

const VehicleDetail: React.FC = () => {
    const { vin } = useParams<{ vin: string }>();
    const { t } = useTranslation();
    const formatDate = useDateFormat();

    const { list: listSettingItems } = useSettingLists();
    const { get: getVehicle, edit: editVehicle } = useVehicle();
    const { list: listServices } = useVehicleService();
    const { list: listTransfers } = useVehicleTransfer();
    const { list: listOdometerHistory } = useOdometerHistory();
    const [activeTab, setActiveTab] = useState(0);
    const [vehicle, setVehicle] = useState<Vehicle | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [odometerHistory, setOdometerHistory] = useState<{ datum: string; km: number; mth: number }[]>([]);
    const [vehicleServices, setVehicleServices] = useState<VehicleService[]>([]);
    const [vehicleTransfers, setVehicleTransfers] = useState<VehicleTransfer[]>([]);
    const [serviceTypeMap, setServiceTypeMap] = useState<Record<string, string>>({});
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [odometerOpen, setOdometerOpen] = useState(false);
    const [nicknameOpen, setNicknameOpen] = useState(false);
    const [nicknameInput, setNicknameInput] = useState('');
    const [savingAvailability, setSavingAvailability] = useState(false);
    const [availabilityError, setAvailabilityError] = useState<string | null>(null);

    const handleToggleAvailableForSale = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!vehicle) return;
        const next = e.target.checked;
        setSavingAvailability(true);
        setAvailabilityError(null);
        try {
            await editVehicle(vehicle.uid, { availableForSale: next });
            setVehicle(v => v ? { ...v, availableForSale: next } : v);
        } catch {
            setAvailabilityError(t('vehicleDetail.sidebar.availableForSaleError'));
        } finally {
            setSavingAvailability(false);
        }
    };

    useEffect(() => {
        listSettingItems('serviceTypes').then((items: SettingListItem[]) => {
            setServiceTypeMap(Object.fromEntries(items.map((i) => [i.id, i.label])));
        });
    }, [listSettingItems]);

    useEffect(() => {
        if (!vin) return;
        const fetch = async () => {
            const [v, odometerRecords, services, transfers] = await Promise.all([
                getVehicle(vin),
                listOdometerHistory({ vehicleUid: vin, order: 'asc' }),
                listServices({ vehicleUid: vin }),
                listTransfers({ vehicleUid: vin }),
            ]);
            if (!v) {
                setNotFound(true);
            } else {
                setVehicle(v);
            }
            setOdometerHistory(odometerRecords.map((rec) => ({
                datum: rec.recordedAt.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' }),
                km: rec.kilometers,
                mth: rec.motohours,
            })));
            setVehicleServices(services);
            setVehicleTransfers(transfers);
            setLoading(false);
        };
        fetch();
    }, [vin, getVehicle, listOdometerHistory, listServices, listTransfers]);

    if (loading) {
        return (
            <Box sx={vehicleDetailStyles.loadingContainer}>
                <CircularProgress />
            </Box>
        );
    }

    if (notFound || !vehicle) {
        return <Alert severity="error">{t('vehicleDetail.notFound', { vin })}</Alert>;
    }

    const hasWarranty = !!vehicle.warranty?.validUntil;
    const isWarrantyValid = hasWarranty && new Date(vehicle.warranty!.validUntil!) > new Date();
    const lastService = vehicleServices[0] ?? null;

    return (
        <Box sx={vehicleDetailStyles.root}>
            <Breadcrumbs>
                <Box component={Link} to="/app" sx={vehicleDetailStyles.homeBreadcrumbLink}>
                    <Home size={20} />
                </Box>
                <Box component={Link} to="/app/garage" sx={vehicleDetailStyles.garageBreadcrumbLink}>
                    {t('vehicleDetail.breadcrumbGarage')}
                </Box>
                <Typography variant="body2" color="text.primary">{vehicle.model}</Typography>
            </Breadcrumbs>

            <Box sx={vehicleDetailStyles.headerRow}>
                <Box sx={vehicleDetailStyles.titleRow}>
                    {vehicle.nickname && (
                        <Typography variant="h4" fontWeight={700}>{vehicle.nickname}</Typography>
                    )}
                    <Typography variant={vehicle.nickname ? 'h6' : 'h4'} fontWeight={vehicle.nickname ? 400 : 700} color={vehicle.nickname ? 'text.secondary' : 'text.primary'}>{vehicle.model}</Typography>
                    <Chip label={vin} size="small" sx={vehicleDetailStyles.vinChip} />
                </Box>
                <Box sx={vehicleDetailStyles.headerActions}>
                    {vehicle.status === VehicleStatus.Active && (
                        <Button variant="contained" startIcon={<Wrench size={20} />} component={Link} to={`/app/vehicles/${vin}/service-request`}>
                            {t('vehicleDetail.orderService')}
                        </Button>
                    )}
                    {vehicle.status === VehicleStatus.InfoRequested && (
                        <Button
                            variant="contained"
                            startIcon={<Send size={20} />}
                            onClick={async () => {
                                await editVehicle(vehicle.uid, { status: VehicleStatus.Pending });
                                setVehicle(v => v ? { ...v, status: VehicleStatus.Pending } : v);
                            }}
                        >
                            {t('vehicleDetail.submitForApproval')}
                        </Button>
                    )}
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Main Content */}
                <Grid item xs={12} lg={8}>
                    <Paper elevation={0} sx={vehicleDetailStyles.card}>
                        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={vehicleDetailStyles.tabs}>
                            <Tab label={t('vehicleDetail.tabs.overview')} sx={vehicleDetailStyles.tab} />
                            <Tab label={t('vehicleDetail.tabs.serviceHistory')} sx={vehicleDetailStyles.tab} />
                            <Tab label={t('vehicleDetail.tabs.transfers')} sx={getVehicleDetailTransferTabStyles(vehicleTransfers.length > 0)} />
                            <Tab label={t('vehicleDetail.tabs.documents')} sx={vehicleDetailStyles.tab} />
                        </Tabs>

                        <Box sx={vehicleDetailStyles.mainContent}>
                            {activeTab === 0 && (
                                <Box sx={vehicleDetailStyles.overviewSection}>
                                    {vehicle.statusNote && (
                                        <>
                                            <Typography variant="subtitle1" fontWeight={600}>{vehicle.status === VehicleStatus.Rejected ? t('vehicleDetail.overview.rejectionReason') : t('vehicleDetail.overview.infoRequested')}</Typography>
                                            <Alert severity={vehicle.status === VehicleStatus.Rejected ? 'error' : 'info'}>
                                                <Typography variant="body2">{vehicle.statusNote}</Typography>
                                            </Alert>
                                        </>
                                    )}
                                    <Typography variant="subtitle1" fontWeight={600}>{t('vehicleDetail.overview.warrantyStatus')}</Typography>
                                    <Alert
                                        severity={!hasWarranty ? 'info' : isWarrantyValid ? 'success' : 'error'}
                                        icon={!hasWarranty ? <Info size={20} /> : isWarrantyValid ? <ShieldCheck size={20} /> : <ShieldX size={20} />}
                                    >
                                        <Typography variant="body2" fontWeight={600}>
                                            {!hasWarranty ? t('vehicleDetail.overview.warrantyNotFilled') : isWarrantyValid ? t('vehicleDetail.overview.warrantyActive') : t('vehicleDetail.overview.warrantyExpired')}
                                        </Typography>
                                        {hasWarranty && (
                                            <Typography variant="body2">
                                                {t('vehicleDetail.overview.warrantyType')}: {vehicle.warranty?.type ?? '—'}<br />
                                                {t('vehicleDetail.overview.warrantyValidUntil')}: {formatDate(vehicle.warranty!.validUntil!)}
                                            </Typography>
                                        )}
                                    </Alert>

                                    <Typography variant="subtitle1" fontWeight={600}>{t('vehicleDetail.overview.lastService')}</Typography>
                                    <Paper variant="outlined" sx={vehicleDetailStyles.lastServicePaper}>
                                        {!lastService ? (
                                            <Typography variant="body2" color="text.secondary">{t('vehicleDetail.overview.noService')}</Typography>
                                        ) : (
                                            <Box sx={vehicleDetailStyles.lastServiceRow}>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">{t('vehicleDetail.overview.dateLabel')}</Typography>
                                                    <Typography variant="body2">{formatDate(lastService.createdAt)}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">{t('vehicleDetail.overview.kmLabel')}</Typography>
                                                    <Typography variant="body2">{lastService.currentMileage !== undefined ? `${lastService.currentMileage} km` : '—'}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">{t('vehicleDetail.overview.mthLabel')}</Typography>
                                                    <Typography variant="body2">{lastService.currentHours !== undefined ? `${lastService.currentHours} mth` : '—'}</Typography>
                                                </Box>
                                                <Box component={Link} to={`/app/vehicles/${vin}/service-request`} sx={vehicleDetailStyles.orderServiceLink}>
                                                    {t('vehicleDetail.overview.orderNext')} <ArrowRight size={20} />
                                                </Box>
                                            </Box>
                                        )}
                                    </Paper>
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <ServiceHistory vin={vin!} services={vehicleServices} serviceTypeMap={serviceTypeMap} />
                            )}

                            {activeTab === 2 && vehicleTransfers.length > 0 && (
                                <TransferHistory transfers={vehicleTransfers} />
                            )}

                            {activeTab === 3 && (
                                <Documents vin={vin!} services={vehicleServices} transfers={vehicleTransfers} serviceTypeMap={serviceTypeMap} />
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Sidebar */}
                <Grid item xs={12} lg={4}>
                    <Box sx={vehicleDetailStyles.sidebar}>
                        {/* Machine Info */}
                        <Paper elevation={0} sx={vehicleDetailStyles.card}>
                            <VehiclePicture vehicle={vehicle} />
                            <Box sx={vehicleDetailStyles.machineInfoContent}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t('vehicleDetail.sidebar.vinLabel')}</Typography>
                                    <Typography variant="body2" fontFamily="monospace">{vehicle.vin}</Typography>
                                </Box>
                                <Box sx={vehicleDetailStyles.odometerRow}>
                                    <Box sx={vehicleDetailStyles.odometerItem}>
                                        <Typography variant="caption" color="text.secondary">{t('vehicleDetail.sidebar.kmLabel')}</Typography>
                                        <Typography variant="body2">{vehicle.odometer ? `${vehicle.odometer.kilometers} km` : '—'}</Typography>
                                    </Box>
                                    <Box sx={vehicleDetailStyles.odometerItem}>
                                        <Typography variant="caption" color="text.secondary">{t('vehicleDetail.sidebar.mthLabel')}</Typography>
                                        <Typography variant="body2">{vehicle.odometer ? `${vehicle.odometer.motohours} h` : '—'}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>

                        {/* Odometer Chart */}
                        <Paper elevation={0} sx={vehicleDetailStyles.card}>
                            <Box sx={vehicleDetailStyles.chartHeader}>
                                <Typography variant="body2" fontWeight={600}>{t('vehicleDetail.sidebar.chartTitle')}</Typography>
                            </Box>
                            <Box sx={vehicleDetailStyles.chartContent}>
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={odometerHistory} margin={{ top: 4, right: 36, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="datum" tick={{ fontSize: 10 }} />
                                        <YAxis yAxisId="km" tick={{ fontSize: 10 }} unit=" km" width={58} />
                                        <YAxis yAxisId="mth" orientation="right" tick={{ fontSize: 10 }} unit=" h" width={32} />
                                        <Tooltip
                                            formatter={(value: number, name: string) =>
                                                name === 'km' ? [`${value} km`, t('vehicleDetail.sidebar.chartKm')] : [`${value} h`, t('vehicleDetail.sidebar.chartMth')]
                                            }
                                        />
                                        <Legend iconSize={10} wrapperStyle={{ fontSize: '11px' }} formatter={(value: string) => value === 'km' ? t('vehicleDetail.sidebar.chartKm') : t('vehicleDetail.sidebar.chartMth')} />
                                        <Line yAxisId="km" type="monotone" dataKey="km" stroke="#009fe3" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                        <Line yAxisId="mth" type="monotone" dataKey="mth" stroke="#f59e0b" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>

                        {/* Other Actions */}
                        <Paper elevation={0} sx={vehicleDetailStyles.card}>
                            <Box sx={vehicleDetailStyles.actionsHeader}>
                                <Typography variant="subtitle2" fontWeight={600}>{t('vehicleDetail.sidebar.actionsTitle')}</Typography>
                            </Box>
                            <Box sx={vehicleDetailStyles.actionsContent}>
                                {vehicle.status === VehicleStatus.Active && (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>{t('vehicleDetail.sidebar.availableForSaleLabel')}</Typography>
                                            <Typography variant="caption" color="text.secondary">{t('vehicleDetail.sidebar.availableForSaleHint')}</Typography>
                                        </Box>
                                        <Switch
                                            checked={!!vehicle.availableForSale}
                                            onChange={handleToggleAvailableForSale}
                                            disabled={savingAvailability}
                                        />
                                    </Box>
                                )}
                                {availabilityError && <Alert severity="error">{availabilityError}</Alert>}
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<Gauge size={20} />}
                                    onClick={() => setOdometerOpen(true)}
                                    sx={vehicleDetailStyles.actionButton}
                                >
                                    {t('vehicleDetail.sidebar.enterOdometer')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<ImagePlus size={20} />}
                                    onClick={() => setUploadDialogOpen(true)}
                                    sx={vehicleDetailStyles.actionButton}
                                >
                                    {t('vehicleDetail.sidebar.uploadImage')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<Tag size={20} />}
                                    onClick={() => { setNicknameInput(vehicle.nickname ?? ''); setNicknameOpen(true); }}
                                    sx={vehicleDetailStyles.actionButton}
                                >
                                    {t('vehicleDetail.sidebar.setNickname')}
                                </Button>
                            </Box>
                        </Paper>

                        <VehiclePictureUploadDialog
                            open={uploadDialogOpen}
                            vehicle={vehicle}
                            onClose={() => setUploadDialogOpen(false)}
                            onSuccess={(url) => setVehicle(v => v ? { ...v, pictureUrl: url } : v)}
                        />
                        <OdometerModal
                            machine={odometerOpen ? vehicle : null}
                            onClose={() => setOdometerOpen(false)}
                            onSaved={(_, odometer) => {
                                setVehicle(v => v ? { ...v, odometer } : v);
                                setOdometerHistory(prev => [...prev, {
                                    datum: odometer.recordedAt.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' }),
                                    km: odometer.kilometers,
                                    mth: odometer.motohours,
                                }]);
                                setOdometerOpen(false);
                            }}
                        />
                    </Box>
                </Grid>
            </Grid>

            <Dialog open={nicknameOpen} onClose={() => setNicknameOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('vehicleDetail.nicknameDialog.title')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {t('vehicleDetail.nicknameDialog.description')}
                    </Typography>
                    <TextField
                        label={t('vehicleDetail.nicknameDialog.label')}
                        fullWidth
                        placeholder={t('vehicleDetail.nicknameDialog.placeholder')}
                        value={nicknameInput}
                        onChange={(e) => setNicknameInput(e.target.value)}
                        inputProps={{ maxLength: 50 }}
                    />
                </DialogContent>
                <DialogActions sx={vehicleDetailStyles.dialogActions}>
                    <Button onClick={() => setNicknameOpen(false)}>{t('common.cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            await editVehicle(vehicle.uid, { nickname: nicknameInput.trim() || undefined });
                            setVehicle(v => v ? { ...v, nickname: nicknameInput.trim() || undefined } : v);
                            setNicknameOpen(false);
                        }}
                    >
                        {t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VehicleDetail;
